/* Ring builder — per-step page controllers.
   One file, dispatched on <div class="rb" data-rb-page="...">, so the theme only
   has to register one script. Depends on ring-builder.js (RingBuilder). */
(function () {
  'use strict';
  var RB = window.RingBuilder;
  if (!RB) return;
  var el = RB.el, money = RB.money;

  // PEARL leads because it is not a cut — a pearl seat takes a pearl and nothing
  // else, and a pearl shopper should not have to hunt past ten diamond cuts.
  // Without it in this list the rail rendered no Pearl entry at all, so the 84
  // pearl pendant settings landed the shopper on an empty grid (owner rule
  // 2026-07-29: only HALF-drilled pearls are offered — a cup or peg needs one).
  var SHAPE_ORDER = ['PEARL', 'ROUND', 'OVAL', 'CUSHION', 'EMERALD', 'PEAR', 'RADIANT',
    'SQUARE', 'ASSCHER', 'MARQUISE', 'HEART', 'ELONGATED CUSHION',
    'ANTIQUE CUSHION', 'TRILLION', 'ELONGATED HEXAGON', 'OCTAGON',
    'ELONGATED OVAL', 'STRAIGHT BAGUETTE'];

  // A pearl is graded on nothing the diamond rail asks about — no carat, colour,
  // clarity or cut. Those filters would sit there matching everything (or nothing)
  // and the card subtitle "1.27 ct · F · VVS1 · Excellent" would render as a row
  // of empty separators. Owner decision 2026-07-29.
  var isPearlBuild = function (build) {
    return String((build && build.seat) || '').indexOf('PEARL|') === 0;
  };
  var title = function (s) {
    return String(s || '').toLowerCase().replace(/(^|\s)\w/g, function (m) { return m.toUpperCase(); });
  };
  // "a emerald center", "a asscher", "a oval". Five of the eighteen shapes start
  // with a vowel, so the count line got this wrong nearly a third of the time.
  var article = function (word) {
    return /^[aeiou]/i.test(String(word || '')) ? 'an' : 'a';
  };

  var CHEVRON = '<svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">' +
    '<path d="M1 1.5 6 6.5 11 1.5" stroke="currentColor" stroke-width="1.3"/></svg>';

  function facet(name, open, body) {
    var f = el('div', { class: 'rb-facet', 'data-open': open ? 'true' : 'false' });
    var btn = el('button', {
      class: 'rb-facet__title', type: 'button', 'aria-expanded': open ? 'true' : 'false',
      onclick: function () {
        var o = f.getAttribute('data-open') === 'true';
        f.setAttribute('data-open', o ? 'false' : 'true');
        btn.setAttribute('aria-expanded', o ? 'false' : 'true');
      }
    }, [el('span', { text: name }), el('span', { class: 'rb-facet__chev', html: CHEVRON })]);
    f.appendChild(btn);
    f.appendChild(el('div', { class: 'rb-facet__body' }, body));
    return f;
  }

  /* ---- visual filter controls, the way both benchmarks draw them ----------
     A stone shape is a SHAPE and a metal is a COLOUR; spelling them out as a
     list of words makes the shopper translate. Silhouettes and swatches are
     what Blue Nile and Brilliant Earth both use, and they read instantly. */
  var SHAPE_SVG = {
    // A pearl IS round, so it needs to read as more than the ROUND circle it would
    // otherwise duplicate — the offset highlight is the one cue that says "pearl"
    // at 24px without a label.
    PEARL: '<circle cx="12" cy="12" r="9.2"/><circle cx="8.8" cy="8.8" r="2.1" fill="#fff" stroke="none" opacity="0.75"/>',
    ROUND: '<circle cx="12" cy="12" r="9.2"/>',
    OVAL: '<ellipse cx="12" cy="12" rx="6.4" ry="9.4"/>',
    CUSHION: '<rect x="3.2" y="3.2" width="17.6" height="17.6" rx="5.2"/>',
    'ANTIQUE CUSHION': '<rect x="3.2" y="3.2" width="17.6" height="17.6" rx="6.4"/>',
    'ELONGATED CUSHION': '<rect x="6" y="2.4" width="12" height="19.2" rx="4.6"/>',
    SQUARE: '<rect x="3.6" y="3.6" width="16.8" height="16.8"/>',
    ASSCHER: '<path d="M8 2.8h8L21.2 8v8L16 21.2H8L2.8 16V8z"/>',
    EMERALD: '<path d="M8.6 2.4h6.8L18.8 5.8v12.4L15.4 21.6H8.6L5.2 18.2V5.8z"/>',
    RADIANT: '<path d="M8.6 3.2h6.8L19.4 7v10L15.4 20.8H8.6L4.6 17V7z"/>',
    PEAR: '<path d="M12 2.4c3.6 4.6 7 8.2 7 12.1a7 7 0 0 1-14 0c0-3.9 3.4-7.5 7-12.1z"/>',
    MARQUISE: '<path d="M12 2.4c4 4.6 6 7.6 6 9.6s-2 5-6 9.6c-4-4.6-6-7.6-6-9.6s2-5 6-9.6z"/>',
    HEART: '<path d="M12 21.2S3.4 15.4 3.4 9.7A4.9 4.9 0 0 1 12 6.9a4.9 4.9 0 0 1 8.6 2.8c0 5.7-8.6 11.5-8.6 11.5z"/>',
    TRILLION: '<path d="M12 3 21 19.5H3z"/>',
    OCTAGON: '<path d="M8.4 3h7.2L21 8.4v7.2L15.6 21H8.4L3 15.6V8.4z"/>',
    'ELONGATED HEXAGON': '<path d="M12 2.4 18 7v10l-6 4.6L6 17V7z"/>',
    'ELONGATED OVAL': '<ellipse cx="12" cy="12" rx="5" ry="9.6"/>',
    'STRAIGHT BAGUETTE': '<rect x="7.5" y="2.6" width="9" height="18.8"/>'
  };

  function shapeIcon(shape) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" ' +
      'stroke-linejoin="round" aria-hidden="true">' + (SHAPE_SVG[shape] || SHAPE_SVG.ROUND) + '</svg>';
  }

  var METAL_SWATCH = {
    '14K Yellow Gold': 'linear-gradient(145deg,#f3dc9a,#d8ad4c)',
    '14K White Gold': 'linear-gradient(145deg,#f6f6f6,#d3d5d8)',
    '14K Rose Gold': 'linear-gradient(145deg,#f4cfc0,#dda289)',
    'Platinum': 'linear-gradient(145deg,#eeeff1,#c7cbd0)'
  };

  function tileGroup(values, selected, onPick, render) {
    return [el('div', { class: 'rb-chipgrid' }, values.map(function (v) {
      return el('button', {
        class: 'rb-chip', type: 'button', 'aria-pressed': String(selected === v),
        onclick: function () { onPick(selected === v ? null : v); }
      }, render(v));
    }))];
  }

  function chipGroup(values, selected, onPick, labelFor, disabledSet) {
    return values.map(function (v) {
      return el('button', {
        class: 'rb-chip', type: 'button',
        'aria-pressed': String(selected === v),
        disabled: disabledSet && disabledSet.has(v) ? 'disabled' : null,
        text: labelFor ? labelFor(v) : v,
        onclick: function () { onPick(selected === v ? null : v); }
      });
    });
  }

  /* ========================================= STEP 1b — setting detail ---- */
  // Same URL as the grid with view=detail, so back/forward works and the address
  // stays shareable. This is where the customer commits to metal, center-stone
  // shape/size and accent origin — i.e. picks the actual Shopify variant.
  function pageSettingDetail(root, build, style) {
    var body = root.querySelector('[data-rb-body]');
    body.innerHTML = '<div class="rb-skel" style="grid-column:1/-1;max-width:40rem"></div>';
    // variants + renders are lazy; wait for them before drawing anything that
    // depends on a price or an angle
    RB.needSettingDetail().then(function () { drawSettingDetail(root, build, style); });
  }

  function drawSettingDetail(root, build, style) {
    var s = RB.setting(style);
    var body = root.querySelector('[data-rb-body]');
    if (!s) {
      body.innerHTML = '<div class="rb-empty">That setting could not be found. ' +
        '<a href="' + RB.pages.settings + '">Back to settings</a>.</div>';
      return;
    }
    var q = new URLSearchParams(location.search);

    /* ---- THE STONE, WHEN THERE IS ONE, DECIDES THE SHAPE AND THE SEAT --------
       Half the people on this page arrived from a stone product page ("Set this
       stone into a ring") and have already chosen their centre. For them the
       centre-stone shape and size are not questions — they are FACTS about the
       stone in their hand, and offering them as free choices is how a build ends
       up as an Emerald stone in a Round 7.40 mm seat. So a resolved stone pins
       both, and the chips for everything it rules out are DISABLED rather than
       hidden, which is the same thing step 2's shape rail does with the seat. */
    var stone = root.__stone || null;
    var wantsStone = !!build.stone;                 // in the URL, resolved or not
    var stoneSeat = stone ? RB.bestSeat(style, stone) : null;

    var shape = (stoneSeat ? stone.shape : null) ||
      q.get('shape') || (s.shapes.indexOf('ROUND') >= 0 ? 'ROUND' : s.shapes[0]);
    // A shape this style does not offer leaves the size facet empty and no variant
    // resolvable — the grid never produces one, but a hand-edited or shared URL can.
    if (s.shapes.indexOf(shape) < 0) {
      shape = s.shapes.indexOf('ROUND') >= 0 ? 'ROUND' : s.shapes[0];
    }
    // NUMERIC sort. The index ships seats string-sorted, which puts
    // 10/11/13/15 mm before 4.1 mm and reads as broken.
    var seatsFor = s.seats.filter(function (k) { return RB.parseSeat(k).shape === shape; })
      .sort(function (a, b) {
        var pa = RB.parseSeat(a), pb = RB.parseSeat(b);
        return (pa.l - pb.l) || (pa.w - pb.w);
      });
    var seat = q.get('seat');
    if (stoneSeat) {
      // an explicitly chosen seat survives only if the stone actually sits in it;
      // several seats can hold one stone, and the shopper may prefer a looser one
      if (!(seat && seatsFor.indexOf(seat) >= 0 && RB.fits(stone, seat))) seat = stoneSeat;
    } else if (seatsFor.indexOf(seat) < 0) {
      // default to a mid-range seat rather than the smallest — the smallest is
      // usually a 4 mm accent size nobody centers a ring on
      seat = seatsFor[Math.floor(seatsFor.length / 2)] || seatsFor[0];
    }
    var metal = q.get('metal') || (s.metals.indexOf('14K Yellow Gold') >= 0 ? '14K Yellow Gold' : s.metals[0]);
    var accent = q.get('accent') || s.accents[0];

    function go(patch) {
      var n = new URLSearchParams(location.search);
      n.set('style', style); n.set('configure', '1');
      Object.keys(patch).forEach(function (k) {
        if (patch[k] == null) n.delete(k); else n.set(k, patch[k]);
      });
      location.search = n.toString();
    }

    // 🚨 START FROM `build`, NEVER FROM A FRESH OBJECT. This was five hand-listed
    // keys, so everything ELSE the build carries — `stone` above all, plus
    // `origin` and `size` — was dropped the moment the shopper left this page.
    // The symptom was the whole point of the deep link being undone: you arrive
    // from a stone product page with the stone chosen, pick a mounting, and step 2
    // asks you to choose a gemstone all over again. A build is not five fields;
    // `RB.FIELDS` says what it is, and copying it wholesale is what keeps this
    // honest when a sixth is added.
    var chosen = Object.assign({}, build, {
      style: style, shape: shape, seat: seat, metal: metal, accent: accent
    });
    var sv = RB.settingVariant(chosen);

    // REDRAW THE STEP BAR AGAINST `chosen`, NOT THE RAW URL. The dispatcher drew
    // it from RB.read() before this function existed, and the seat/metal/accent
    // this page DEFAULTS to are not in the URL until something calls go(). So on
    // a freshly opened setting (?style=…&configure=1) step 3 linked to review
    // with no seat, and review answered "Start by choosing a setting" — from a
    // link the shopper had every reason to think was ready.
    // A stone this style cannot hold is dropped from the bar's build for the same
    // reason the CTA below drops it: otherwise the big button says "choose a
    // gemstone" while the bar two inches above still offers "review and add to
    // cart" on the pair we just said is impossible.
    var stepsMount = root.querySelector('[data-rb-steps]');
    if (stepsMount) {
      RB.stepBar(stepsMount,
        (wantsStone && !stoneSeat) ? Object.assign({}, chosen, { stone: null }) : chosen, 1);
    }

    // `--detail` is what wins back the two-column layout that the settings page's
    // `display:block` override takes away — see ring-builder.css.
    body.className = 'rb-layout rb-layout--detail';
    body.innerHTML = '';

    // --- media: the five real angles for THIS seat and metal
    var stage = el('div', { class: 'rb-stage' });
    var thumbs = el('div', { class: 'rb-angles' });
    var angles = RB.anglesFor(style, seat, metal);
    if (angles.length) {
      angles.forEach(function (a, i) {
        stage.appendChild(el('img', { src: a.url, alt: s.title + ' — ' + a.angle, class: i === 0 ? 'on' : '' }));
      });
      angles.forEach(function (a, i) {
        thumbs.appendChild(el('button', {
          class: 'rb-angle', type: 'button', 'aria-pressed': String(i === 0),
          onclick: function () {
            stage.querySelectorAll('img').forEach(function (im, j) { im.classList.toggle('on', j === i); });
            thumbs.querySelectorAll('.rb-angle').forEach(function (b, j) { b.setAttribute('aria-pressed', String(j === i)); });
          }
        }, [el('img', { src: a.url, alt: a.angle, loading: 'lazy' })]));
      });
    } else {
      var fallback = s.imgByMetal[metal] || s.img;
      if (fallback) stage.appendChild(el('img', { src: fallback, alt: s.title, class: 'on' }));
    }
    body.appendChild(el('div', {}, [stage, thumbs]));

    // --- options
    var panel = el('div', {});
    panel.appendChild(el('h2', { class: 'h4', text: s.title.replace(/ Setting$/, '') }));
    panel.appendChild(el('p', {
      class: 'rb-card__price rb-setting-price',
      text: sv ? RB.money(sv[1]) : 'From ' + RB.money(s.min),
      style: 'font-size:var(--text-size-large);margin:.6rem 0 2rem'
    }));

    // Your gemstone, restated on the page that would otherwise look like it forgot
    // it. The shopper who deep-linked here has no other confirmation that the
    // stone travelled with them, and "did it keep my stone?" is exactly the doubt
    // that sends someone back to step 2 to pick it a second time.
    if (stone && stoneSeat) {
      panel.appendChild(el('div', { class: 'rb-chosen' }, [
        stone.img ? el('img', { class: 'rb-chosen__img', src: stone.img, alt: '', loading: 'lazy' }) : null,
        el('span', { class: 'rb-chosen__body' }, [
          el('span', { class: 'rb-chosen__lbl', text: 'Your gemstone' }),
          el('span', { class: 'rb-chosen__title', text: stone.title }),
          el('span', { class: 'rb-chosen__sub', text: money(stone.price) })
        ]),
        el('a', {
          class: 'rb-chosen__change',
          href: RB.href(build.origin === 'lab' ? RB.pages.stonesLab : RB.pages.stones,
                        chosen, { stone: null, seat: null }),
          text: 'Change'
        })
      ]));
    }

    var shapeChoices = SHAPE_ORDER.filter(function (x) { return s.shapes.indexOf(x) >= 0; });
    var lockedShapes = null;
    if (stoneSeat) {
      // SHAPE_ORDER is a hand-kept list, so a shape it has never heard of is
      // dropped here. Harmless while every chip is live; with everything else
      // disabled it would leave a facet of nothing but greyed-out chips.
      if (shapeChoices.indexOf(stone.shape) < 0) shapeChoices.push(stone.shape);
      lockedShapes = new Set(shapeChoices.filter(function (x) { return x !== stone.shape; }));
    }
    panel.appendChild(facet('Center stone shape', true,
      chipGroup(shapeChoices, shape,
        function (v) { if (v) go({ shape: v, seat: null }); }, title, lockedShapes)));

    // Seats the chosen stone will not physically sit in are disabled for the same
    // reason: this is the control that picks the real Stuller mounting SKU.
    var lockedSeats = null;
    if (stoneSeat) {
      lockedSeats = new Set(seatsFor.filter(function (k) { return !RB.fits(stone, k); }));
    }
    panel.appendChild(facet('Center stone size', true,
      chipGroup(seatsFor, seat, function (v) { if (v) go({ seat: v }); },
        function (k) { var p2 = RB.parseSeat(k); return p2.l === p2.w ? p2.l + ' mm' : p2.l + '×' + p2.w; },
        lockedSeats)));

    if (stoneSeat) {
      panel.appendChild(el('p', { class: 'rb-facet__hint', style: 'margin:-1rem 0 1.6rem' },
        [el('span', { text: 'Cut to hold your ' + stone.l.toFixed(2) + ' × ' +
                             (stone.w || stone.l).toFixed(2) + ' mm ' +
                             title(stone.shape).toLowerCase() + '. ' }),
         el('a', { href: RB.href(build.origin === 'lab' ? RB.pages.stonesLab : RB.pages.stones,
                                 chosen, { stone: null, seat: null }),
                   text: 'Choose a different gemstone' })]));
    }

    panel.appendChild(facet('Metal', true,
      chipGroup(s.metals, metal, function (v) { if (v) go({ metal: v }); },
        function (m) { return m.replace('14K ', '').replace(' Gold', ''); })));

    if (s.accents.length > 1) {
      panel.appendChild(facet('Accent diamonds', true,
        chipGroup(s.accents, accent, function (v) { if (v) go({ accent: v }); })));
    }

    /* ---- where this button goes depends on whether a stone came with us -------
       Three states, and only the first is the old one:
         no stone           -> step 2, "Choose a gemstone"
         stone that fits    -> STEP 3. Sending these shoppers to step 2 is what
                               made the deep link feel broken: they had already
                               chosen, and the funnel asked again.
         stone that does not fit / did not resolve -> say so, and offer the swap. */
    var piece = RB.KINDS[RB.kind(build)].label;
    var nextPath = RB.pages.stones, nextLabel = 'Choose a gemstone →', nextExtra = { configure: null };
    var warn = null;

    if (stoneSeat) {
      nextPath = RB.pages.review;
      nextLabel = 'Review your ' + piece + ' →';
    } else if (wantsStone && !stone) {
      // in the URL but not in any shard — a sold serial, or a link from before a
      // recut. Never carry a stone we cannot price into the next step.
      nextExtra.stone = null;
      warn = 'We could not find the gemstone from your link — every serialized stone ' +
             'here is one of one, so it may have just sold. Choose another and the ' +
             'setting you picked stays with you.';
    } else if (stone) {
      nextExtra.stone = null;
      warn = 'This setting cannot be cut for your ' + title(stone.shape).toLowerCase() +
             ' at ' + stone.l.toFixed(2) + ' × ' + (stone.w || stone.l).toFixed(2) +
             ' mm. Pick another setting, or choose a different gemstone for this one.';
    }
    if (warn) panel.appendChild(el('div', { class: 'rb-note', text: warn }));

    var next = el('a', {
      class: 'btn btn-primary',
      style: 'width:100%;margin-top:2rem;display:block;text-align:center',
      href: RB.href(nextPath, chosen, Object.assign({ shape: shape }, nextExtra)),
      text: nextLabel
    });
    panel.appendChild(next);
    if (warn && stone) {
      panel.appendChild(el('p', { class: 'rb-detail__back', style: 'margin-top:1.2rem' }, [
        el('a', { href: RB.href(RB.pages.settings, build, { style: null, configure: null }),
                  text: '← Back to the settings that fit this stone' })
      ]));
    }
    panel.appendChild(el('p', {
      class: 'text-size-tiny color-text-80', style: 'margin-top:1rem',
      text: stoneSeat
        ? 'This price is for the setting alone. Your gemstone is listed separately, ' +
          'and you will see both before you pay.'
        : 'This price is for the setting alone. Your center stone is chosen next and priced separately.'
    }));
    body.appendChild(panel);
  }

  /* =============================================== STEP 1 — settings grid */
  function pageSettings(root, build) {
    var q = new URLSearchParams(location.search);
    // The grid and the detail view share one URL; `configure=1` switches.
    // NOT `view=` — that is a RESERVED Shopify parameter that selects an
    // alternate template (page.rb-settings.detail). It does not exist, so
    // Shopify silently falls back to the default page template and the whole
    // section vanishes. Verified: ?foo=bar renders fine, ?view=detail does not.
    var isDetail = !!(q.get('configure') && q.get('style'));

    /* ---- a stone in the URL is a CONSTRAINT on this step, not a passenger -----
       Arriving from "Set this stone into a ring" showed all 51 ring settings (140
       pendant mountings) when only 14 (19) could hold the stone. Every one of the
       other 37 was a dead end that ended in "that gemstone will not sit in this
       setting size" — or, before the guards added with it, in a cart line the
       bench would have had to reject. Fit is the same test step 2 applies in the
       other direction (RB.fitsStyle: does ANY seat of this style take the stone),
       so neither surface can drift from the other.

       BOTH views wait. The dispatcher re-runs this once the shard is in; drawing
       first would flash 51 settings before cutting to 14, and the detail view
       would announce "we could not find the gemstone" about one that is merely
       still downloading. */
    if (root.__stonePending) {
      var body0 = root.querySelector('[data-rb-body]');
      if (isDetail) {
        if (body0) body0.innerHTML = '<div class="rb-skel" style="grid-column:1/-1;max-width:40rem"></div>';
        return;
      }
      var g0 = root.querySelector('[data-rb-grid]');
      var c0 = root.querySelector('[data-rb-count]');
      if (c0) c0.textContent = 'Finding the settings that fit your gemstone…';
      if (g0) {
        g0.innerHTML = '';
        for (var k0 = 0; k0 < 6; k0++) g0.appendChild(el('div', { class: 'rb-skel' }));
      }
      return;
    }
    if (isDetail) return pageSettingDetail(root, build, q.get('style'));

    var stone = root.__stone || null;
    var showAll = q.get('all') === '1';
    var all = RB.settings();
    var fitting = stone ? all.filter(function (s) { return RB.fitsStyle(s.style, stone); }) : null;
    // `?all=1` is the escape hatch, and it has to exist: a shopper is allowed to
    // decide the setting matters more than the stone they clicked in on, and a
    // grid with no way out of its own filter is a trap.
    // EVERYTHING below reads `pool`, not `all` — the style strip and the shape
    // facet included. A strip still counting all 51 beside a grid of 14 is the
    // same lie in a different font, and its tiles would filter to nothing.
    var pool = (fitting && !showAll) ? fitting : all;
    var fShape = q.get('shape') || build.shape || null;
    var fFamily = q.get('family') || null;
    var fMetal = q.get('metal') || null;
    var sort = q.get('sort') || 'price-asc';

    var rail = root.querySelector('[data-rb-rail]');
    var grid = root.querySelector('[data-rb-grid]');
    var count = root.querySelector('[data-rb-count]');
    var strip = root.querySelector('[data-rb-styles]');
    var sortWrap = root.querySelector('[data-rb-sort]');

    function apply(patch) {
      var n = new URLSearchParams(location.search);
      Object.keys(patch).forEach(function (k) {
        if (patch[k] == null) n.delete(k); else n.set(k, patch[k]);
      });
      location.search = n.toString();
    }

    var shapes = {};
    pool.forEach(function (s) { s.shapes.forEach(function (x) { shapes[x] = 1; }); });
    var shapeList = SHAPE_ORDER.filter(function (x) { return shapes[x]; })
      .concat(Object.keys(shapes).filter(function (x) { return SHAPE_ORDER.indexOf(x) < 0; }));
    var metals = ['14K Yellow Gold', '14K White Gold', '14K Rose Gold', 'Platinum'];

    // `family` is a LIST — a design can be both Accented and Solitaire — so
    // membership is "contains", never equality. (It was equality against a value
    // that was always null, which is why the old Style facet matched nothing.)
    var FAMILIES = [
      { key: 'solitaire', label: 'Solitaire' },
      { key: 'halo', label: 'Halo' },
      { key: 'three-stone', label: 'Three Stone' },
      { key: 'accented', label: 'Accented' }
    ];
    var inFamily = function (s, k) { return (s.family || []).indexOf(k) >= 0; };

    /* ---- the style strip: pictures first, the way both benchmarks lead ---- */
    if (strip) {
      strip.innerHTML = '';
      var tiles = [{ key: null, label: pool === all ? 'All settings' : 'All that fit',
                     n: pool.length, img: (pool[0] || {}).img }];
      FAMILIES.forEach(function (f) {
        var members = pool.filter(function (s) { return inFamily(s, f.key); });
        if (!members.length) return;
        var withImg = members.filter(function (s) { return s.img; })[0] || members[0];
        tiles.push({ key: f.key, label: f.label, n: members.length, img: withImg.img });
      });
      // …then the marketing cuts off WebCategories (Vintage, Bezel-Set, …). These
      // are what a shopper actually browses by and what the benchmark shows;
      // Stuller's six `family` words alone are far too coarse a strip.
      var cueCounts = {};
      pool.forEach(function (s) {
        (s.cues || []).forEach(function (c) { cueCounts[c] = (cueCounts[c] || 0) + 1; });
      });
      Object.keys(cueCounts).sort(function (a, b) { return cueCounts[b] - cueCounts[a]; })
        .forEach(function (c) {
          var members = pool.filter(function (s) { return (s.cues || []).indexOf(c) >= 0; });
          var withImg = members.filter(function (s) { return s.img; })[0] || members[0];
          tiles.push({ key: 'cue:' + c, label: c.replace(/-/g, ' '),
                       n: members.length, img: withImg && withImg.img });
        });
      var track = el('div', { class: 'rb-styles__track' });
      tiles.forEach(function (tl) {
        track.appendChild(el('button', {
          class: 'rb-style', type: 'button',
          'aria-pressed': String((fFamily || null) === tl.key),
          onclick: function () { apply({ family: tl.key }); }
        }, [
          el('span', { class: 'rb-style__media' }, [
            tl.img ? el('img', { src: tl.img, alt: '', loading: 'lazy' }) : null
          ]),
          el('span', { class: 'rb-style__label', text: tl.label }),
          el('span', { class: 'rb-style__n', text: String(tl.n) })
        ]));
      });
      strip.appendChild(track);
    }

    rail.innerHTML = '';
    rail.appendChild(facet('Center stone shape', true,
      tileGroup(shapeList, fShape, function (v) { apply({ shape: v }); }, function (v) {
        return [el('span', { class: 'rb-chip__icon', html: shapeIcon(v) }),
                el('span', { class: 'rb-chip__cap', text: title(v) })];
      })));
    rail.appendChild(facet('Metal', true,
      tileGroup(metals, fMetal, function (v) { apply({ metal: v }); }, function (v) {
        return [el('span', { class: 'rb-chip__swatch', style: 'background:' + METAL_SWATCH[v] }),
                el('span', { class: 'rb-chip__cap', text: v.replace('14K ', '') })];
      })));

    // `family` carries either a family key or a `cue:Vintage`-style marker; one
    // URL parameter drives the whole strip so a shared link is unambiguous.
    var cueFilter = fFamily && fFamily.indexOf('cue:') === 0 ? fFamily.slice(4) : null;
    var rows = pool.filter(function (s) {
      if (fShape && s.shapes.indexOf(fShape) < 0) return false;
      if (cueFilter) { if ((s.cues || []).indexOf(cueFilter) < 0) return false; }
      else if (fFamily && !inFamily(s, fFamily)) return false;
      if (fMetal && s.metals.indexOf(fMetal) < 0) return false;
      return true;
    });
    rows.sort(function (a, b) {
      return sort === 'price-desc' ? b.min - a.min
           : sort === 'name' ? a.title.localeCompare(b.title) : a.min - b.min;
    });

    count.innerHTML = '';
    var piece1 = RB.KINDS[RB.kind(build)].mount;   // "setting" / "mounting"
    count.appendChild(el('span', { html: '<strong>' + rows.length + '</strong> ' + piece1 +
      (rows.length === 1 ? '' : 's') +
      (fitting && !showAll ? ' that fit your gemstone'
                           : (fShape ? ' that take ' + article(fShape) + ' ' +
                                       title(fShape).toLowerCase() + ' center' : '')) }));

    // SAY WHAT WAS FILTERED OUT AND OFFER THE WAY BACK. A silently shortened grid
    // is indistinguishable from a small catalogue.
    if (fitting) {
      count.appendChild(el('span', { class: 'rb-count__note' }, showAll
        ? [el('span', { text: 'Showing every ' + piece1 + ' — ' + (all.length - fitting.length) +
                              ' cannot be cut for your gemstone. ' }),
           el('button', { type: 'button', class: 'rb-linkish', text: 'Show only the ' +
                          fitting.length + ' that fit',
             onclick: function () { apply({ all: null }); } })]
        : [el('span', { text: stone.title + '. ' }),
           el('button', { type: 'button', class: 'rb-linkish',
             text: 'Show all ' + all.length + ' ' + piece1 + 's instead',
             onclick: function () { apply({ all: '1' }); } })]));
    }

    var SORTS = [
      { value: 'price-asc', label: 'Price: low to high' },
      { value: 'price-desc', label: 'Price: high to low' },
      { value: 'name', label: 'Name' }
    ];
    RB.mountSort(sortWrap, SORTS, sort, function (v) { apply({ sort: v }); });

    // Filters are a drawer at EVERY width here (BE pattern), and Sort already
    // sits beside the button in the same bar — so no duplicate sort control.
    RB.mountFilterDrawer(root, rail, {
      activeCount: [fShape, fMetal].filter(Boolean).length,
      countLabel: rows.length + ' Result' + (rows.length === 1 ? '' : 's'),
      applyLabel: 'Show ' + rows.length + ' ' + piece1 + (rows.length === 1 ? '' : 's')
    });

    grid.innerHTML = '';
    if (!rows.length) {
      // Distinguish "your filters are too tight" from "nothing we stock is cut for
      // this stone" — they need opposite next moves, and the second one is the
      // bespoke conversation, not a filter to clear.
      grid.appendChild(fitting && !fitting.length
        ? el('div', { class: 'rb-empty' }, [
            el('p', { text: 'No ' + piece1 + ' we stock is cut for ' + stone.title + '.' }),
            el('p', { text: 'A stone this size is usually set by commission. Tell us what ' +
                            'you have in mind and we will quote it.' }),
            el('a', { class: 'btn btn-primary', style: 'margin-top:1.6rem',
                      href: '/pages/sourcing-request', text: 'Commission a ' + piece1 })
          ])
        : el('div', { class: 'rb-empty', text: 'No ' + piece1 + 's match those filters. Try clearing one.' }));
      return;
    }
    rows.forEach(function (s) {
      var img = (fMetal && s.imgByMetal[fMetal]) || s.img;
      grid.appendChild(el('a', {
        class: 'rb-card',
        href: RB.href(RB.pages.settings, build, { style: s.style, shape: fShape || null, configure: '1' })
      }, [
        el('span', { class: 'rb-card__media' }, [
          img ? el('img', { src: img, alt: s.title, loading: 'lazy' })
              : el('span', { class: 'rb-noimg', text: 'No image' })
        ]),
        el('span', { class: 'rb-card__body' }, [
          el('span', { class: 'rb-card__title', text: s.title.replace(/ Setting$/, '') }),
          el('span', { class: 'rb-card__price rb-setting-price', text: 'From ' + money(s.min) }),
          el('span', { class: 'rb-card__note', text: s.shapes.length + ' center shapes · ' + s.metals.length + ' metals' })
        ])
      ]));
    });
  }

  /* ============================================ STEP 2 — gemstone browser */
  // Lives in ring-builder-stones.js — it is the most involved surface in the
  // funnel and follows its own spec (two rails, ordered-scale 4C sliders,
  // preset pills, seat-aware "best match" sort).
  function pageStones(root, build, origin) {
    if (RB.mountStones) return RB.mountStones(root, build, origin);
    root.querySelector('[data-rb-grid]').innerHTML =
      '<div class="rb-empty">The gemstone browser failed to load. Please refresh.</div>';
  }

  /* ========================================= STEP 2b — one gemstone, in full */
  function pageStoneDetail(root, build, stone) {
    if (RB.mountStoneDetail) return RB.mountStoneDetail(root, build, stone);
    var b = root.querySelector('[data-rb-body]');
    if (b) b.innerHTML = '<div class="rb-empty">This gemstone failed to load. Please refresh.</div>';
  }

  /* ============================================== STEP 3 — review the ring */
  function pageReview(root, build) {
    var setting = RB.setting(build.style);
    var stage = root.querySelector('[data-rb-stage]');
    var angleBar = root.querySelector('[data-rb-angles]');
    var summary = root.querySelector('[data-rb-summary]');
    var cta = root.querySelector('[data-rb-cta]');
    if (!setting || !build.seat) {
      root.querySelector('[data-rb-body]').innerHTML =
        '<div class="rb-empty">Start by choosing a setting.</div>';
      return;
    }
    var sv = RB.settingVariant(build);
    // NO VARIANT FOR THIS COMBINATION. `settingPrice` below falls back to 0, so
    // the page used to show the setting at $0, a total that silently omitted it,
    // and a live "Add to cart" that then rejected with 'setting variant not
    // found'. Reachable from any shared or hand-edited URL — e.g. an accent the
    // style does not offer — and the whole builder is built on shareable URLs.
    // Say so and stop, rather than quoting a price we cannot sell.
    if (!sv) {
      root.querySelector('[data-rb-body]').innerHTML =
        '<div class="rb-empty">That combination is not available. ' +
        'Please <a href="' + RB.href(RB.pages.settings, build, { configure: 1 }) +
        '">choose the setting options again</a>.</div>';
      if (cta) { cta.disabled = true; cta.hidden = true; }
      return;
    }

    /* 🚨 A STONE IN THE URL THAT WE COULD NOT RESOLVE IS NOT "NO STONE CHOSEN".
       It rendered as exactly that: the summary said "No center stone chosen", the
       total quoted the setting alone — $515 against a build carrying a $3,065
       diamond — and Add to cart stayed LIVE, because addToCart reads
       `build.stone` (present) while the fit guard reads `root.__stone` (null) and
       therefore never runs. So the one build that reached the cart unchecked was
       the one we understood least. Reachable from any shared link, and it was the
       ordinary case for anything arriving from a stone product page.
       `__stonePending` is the still-loading state and must NOT trip this. */
    if (build.stone && !root.__stone && root.__stoneResolved) {
      root.querySelector('[data-rb-body]').innerHTML =
        '<div class="rb-empty"><p>We could not find the gemstone from this link.</p>' +
        '<p>Every serialized stone here is one of one, so it may have just sold. ' +
        'Your ' + RB.KINDS[RB.kind(build)].label + ' setting is safe — ' +
        '<a href="' + RB.href(build.origin === 'lab' ? RB.pages.stonesLab : RB.pages.stones,
                              build, { stone: null }) +
        '">choose another gemstone</a> and it will be waiting.</p></div>';
      if (cta) { cta.disabled = true; cta.hidden = true; }
      return;
    }
    var angles = RB.anglesFor(build.style, build.seat, build.metal);

    // five discrete angles, not a continuous spin: Stuller ships no frame
    // sequence for mountings, and interpolating between five frames looks worse
    // than letting the customer click through them
    stage.innerHTML = '';
    if (angles.length) {
      angles.forEach(function (a, i) {
        stage.appendChild(el('img', { src: a.url, alt: setting.title + ' — ' + a.angle, class: i === 0 ? 'on' : '' }));
      });
      angleBar.innerHTML = '';
      angles.forEach(function (a, i) {
        angleBar.appendChild(el('button', {
          class: 'rb-angle', type: 'button', 'aria-pressed': String(i === 0),
          onclick: function () {
            stage.querySelectorAll('img').forEach(function (im, j) { im.classList.toggle('on', j === i); });
            angleBar.querySelectorAll('.rb-angle').forEach(function (b, j) { b.setAttribute('aria-pressed', String(j === i)); });
          }
        }, [el('img', { src: a.url, alt: a.angle, loading: 'lazy' })]));
      });
    } else if (setting.img) {
      stage.appendChild(el('img', { src: setting.img, alt: setting.title, class: 'on' }));
    }

    // 🚨 MULTIPLY BY THE PAIR QUANTITY OR THE REVIEW PAGE UNDER-QUOTES BY HALF.
    // A Shopify variant price is per UNIT, and Stuller sells an earring mounting
    // as ONE earring — so `addToCart` posts quantity 2 for the earring wave while
    // `sv[1]` and `stone.price` are still single-unit numbers. Adding them raw
    // would show a customer $X and charge them $2X at the cart: the same class of
    // silent under-quote as the unresolved-stone bug above, and it appears the
    // day the earring wave goes live rather than being introduced later.
    // 1 for rings and pendants, so both are untouched.
    var qty = RB.pairQty(build);
    var settingPrice = (sv ? sv[1] : 0) * qty;
    var stone = root.__stone || null;
    var stonePrice = (stone ? stone.price : 0) * qty;
    // The shard is still in flight. pageReview runs once synchronously and again
    // when it lands, and on that first pass a build WITH a stone looks exactly
    // like a build without one — so the page would state "No center stone chosen"
    // and a total that is the setting alone. Both are wrong rather than merely
    // early, and the wait is up to seven seconds when the fallback has to walk
    // every shard. Say "loading" and quote nothing until we can quote it all.
    var loadingStone = !!build.stone && !root.__stoneResolved;
    summary.innerHTML = '';
    summary.appendChild(el('div', { class: 'rb-line' }, [
      el('span', {}, [
        el('span', { text: setting.title.replace(/ Setting$/, '') }),
        el('span', { class: 'rb-line__sub', text: build.metal + ' · ' + RB.seatLabel(build.seat) +
          (build.accent && build.accent !== 'None' ? ' · ' + build.accent + ' accents' : '') })
      ]),
      el('span', { class: 'rb-line__amt', text: money(settingPrice) })
    ]));
    summary.appendChild(el('div', { class: 'rb-line' }, [
      el('span', {}, [
        el('span', { text: stone ? stone.title
                                 : (loadingStone ? 'Loading your gemstone…' : 'No center stone chosen') }),
        el('span', { class: 'rb-line__sub', text: stone
          ? ['Center stone', stone.lab && stone.cert ? stone.lab + ' ' + stone.cert : null,
             stone.l ? stone.l.toFixed(2) + ' × ' + (stone.w || stone.l).toFixed(2) + ' mm' : null]
              .filter(Boolean).join(' · ')
          : (loadingStone ? 'One moment' : 'Go back to step 2') })
      ]),
      el('span', { class: 'rb-line__amt', text: stone ? money(stonePrice) : (loadingStone ? '…' : '—') })
    ]));
    summary.appendChild(el('div', { class: 'rb-total' }, [
      el('span', { text: RB.pieceCopy('Your ring', build) }),
      el('span', { class: 'rb-total__amt',
        text: loadingStone ? '…' : money(settingPrice + stonePrice) })
    ]));
    // and the button cannot be live over a total we are not showing
    if (cta) {
      cta.disabled = !!loadingStone;
      cta.textContent = loadingStone ? 'Loading…' : 'Add to cart';
    }

    /* ---- ring size: REQUIRED, and until now never asked for ---------------
       RB.addToCart has always sent `build.size || 'To be confirmed'` and nothing
       ever set build.size, so every custom ring would have been ordered with no
       finger size. The list is per style (see RB.ringSizes) and the choice goes
       into the URL like every other part of the build, so a shared link carries
       the size too. */
    var sizes = RB.ringSizes(build.style);
    var sizeErr = el('p', { class: 'rb-size__err', hidden: 'hidden',
      text: 'Choose a ring size first.' });
    if (sizes && sizes.length) {
      var sel = el('select', { class: 'rb-size__sel', id: 'rb-size',
        'aria-describedby': 'rb-size-help' });
      sel.appendChild(el('option', { value: '', text: 'Select a size' }));
      sizes.forEach(function (s) {
        var o = el('option', { value: String(s), text: RB.sizeLabel(s) });
        if (build.size && String(build.size) === String(s)) o.selected = true;
        sel.appendChild(o);
      });
      sel.addEventListener('change', function () {
        build.size = sel.value || null;
        sizeErr.hidden = true;
        // keep the URL authoritative — the build must stay shareable
        try {
          var u = new URL(location.href);
          if (build.size) u.searchParams.set('size', build.size);
          else u.searchParams.delete('size');
          history.replaceState(null, '', u);
        } catch (e) {}
      });
      summary.appendChild(el('div', { class: 'rb-size' }, [
        el('label', { for: 'rb-size', class: 'rb-size__lbl', text: 'Ring size (US)' }),
        sel,
        sizeErr,
        el('p', { class: 'rb-size__help', id: 'rb-size-help',
          text: 'Not sure? Choose your best guess — we confirm the size with you ' +
                'before the ring goes to the bench, and the first resize is on us.' })
      ]));
    }

    // the durability note only appears when there is something worth saying —
    // an "excellent" stone gets no note at all rather than reassurance nobody asked for
    if (stone && stone.wear && stone.wear.indexOf('Excellent') < 0) {
      summary.appendChild(el('div', { class: 'rb-note' }, [
        el('strong', { text: stone.wear + '. ' }),
        el('span', { text: 'This is a softer or more characterful stone — worth knowing before you commit. We are glad to talk it through.' })
      ]));
    }

    // WIRE THE CTA EXACTLY ONCE. pageReview runs twice whenever the build has a
    // stone — once synchronously from the dispatcher, then again after
    // resolveStone comes back — and the CTA button is a sibling of the summary,
    // so it survives the innerHTML reset. Two listeners on one node meant one
    // click fired addToCart TWICE: four line items and two _build_ids for one
    // ring. Read the stone from root.__stone at click time rather than closing
    // over it, because on the first pass it is still null.
    if (cta.__rbWired) return;
    cta.__rbWired = true;
    cta.addEventListener('click', function () {
      var chosen = root.__stone || null;
      // THE SAME CHECK AS ABOVE, AT CLICK TIME. The block above runs on render,
      // and the first render happens before the shard is back — so between the
      // two there is a live button on a build whose stone we have not priced.
      // Everything the click does downstream (the fit test, the total the
      // customer just read) assumes this is not null.
      if (build.stone && !chosen) {
        var boxS = root.querySelector('[data-rb-error]');
        if (boxS) {
          boxS.textContent = root.__stoneResolved
            ? 'We could not find that gemstone — it may have just sold. Choose another and your setting stays with you.'
            : 'Still loading your gemstone — one moment, then try again.';
          boxS.hidden = false;
        }
        return;
      }
      // Look these up at CLICK time, not from the closure: pageReview rebuilds
      // the summary on its second pass, so the nodes captured on the first pass
      // are detached and writing to them would show the customer nothing.
      var errNode = root.querySelector('.rb-size__err');
      var selNode = root.querySelector('.rb-size__sel');
      if (selNode && !build.size) {
        if (errNode) errNode.hidden = false;
        selNode.focus();
        return;
      }
      // LAST LINE OF DEFENCE ON FIT. The grid filters and the seat snaps to the
      // stone, but a build also arrives here from a shared/edited URL, and the
      // seat is what picks the real Stuller mounting SKU. Never post a pair the
      // bench cannot make.
      if (chosen && build.seat && !RB.fits(chosen, build.seat)) {
        var box0 = root.querySelector('[data-rb-error]');
        if (box0) {
          box0.textContent = 'That gemstone will not sit in this setting size (' +
            RB.seatLabel(build.seat) + ' vs ' + chosen.l.toFixed(2) + ' × ' +
            (chosen.w || chosen.l).toFixed(2) + ' mm). Go back to step 2 and pick ' +
            'a stone for this setting, and we will match the seat for you.';
          box0.hidden = false;
        }
        return;
      }
      cta.disabled = true;
      cta.textContent = 'Adding…';
      RB.addToCart(build, {
        stoneVariantId: Number(build.stone),
        stoneTitle: chosen ? chosen.title : ''
      }).then(function () {
        location.href = '/cart';
      }).catch(function (e) {
        cta.disabled = false;
        cta.textContent = 'Add to cart';
        var box = root.querySelector('[data-rb-error]');
        if (box) {
          box.textContent = e.message === 'no stone chosen'
            ? 'Choose a center stone first.'
            : 'That did not go through — ' + e.message + '. Your build is safe; try again.';
          box.hidden = false;
        }
      });
    });
  }

  /* ------------------------------------------------------------- dispatch */
  document.querySelectorAll('.rb[data-rb-page]').forEach(function (root) {
    var build = RB.read();
    var page = root.getAttribute('data-rb-page');
    var bar = root.querySelector('[data-rb-steps]');
    var activeStep = page === 'settings' ? 1 : (page === 'stones' || page === 'stone') ? 2 : 3;
    // STEP 2 DECLARES ITS OWN ORIGIN AND THAT DECLARATION WINS, the same way
    // data-rb-kind beats a stale ?kind=. The Natural/Lab-grown toggle is a link
    // that carries the WHOLE query string across (see ring-builder.liquid), so
    // `origin=lab` rides onto the natural page and stays there — leaving the step
    // bar pointing back at lab from a page showing natural stones. Now that the
    // deep link from a stone product page emits `origin`, that is the common case
    // rather than a curiosity.
    if (page === 'stones') {
      build.origin = root.getAttribute('data-rb-origin') === 'lab' ? 'lab' : 'nat';
    }
    if (bar) RB.stepBar(bar, build, activeStep);
    // Hero copy is Liquid-rendered before `kind` is known — swap the piece word
    // now so a pendant build never reads "Design your ring". Section scope only.
    RB.applyPieceCopy(root.closest('.shopify-section') || root, build);
    // the chosen stone lives in a shard; resolve it, then re-render the step bar
    // with its real title and price instead of a placeholder
    if (build.stone) {
      // STEP 1 NOW DEPENDS ON THE STONE TOO — it filters the grid to the settings
      // that can hold it. `__stonePending` is what stops pageSettings drawing all
      // 51 first and cutting to 14 a beat later. `__stoneResolved` is separate and
      // says the answer is IN, null or not: the review page has to tell "still
      // loading" apart from "this stone is gone", and they look identical from
      // `__stone` alone.
      root.__stonePending = true;
      root.__stoneResolved = false;
      RB.resolveStone(build).then(function (st) {
        root.__stonePending = false;
        root.__stoneResolved = true;
        if (st) {
          root.__stone = st;
          window.RB_STONE_TITLE = st.title;
          window.RB_STONE_PRICE = st.price;
        }
        if (bar) RB.stepBar(bar, build, activeStep);
        if (page === 'review') { try { pageReview(root, build); } catch (e) {} }
        // step 2b renders ONLY from here — a resolve that comes back empty is a
        // real state (the stone just sold) and has to reach its own empty view,
        // so unlike review it must not bail on a null.
        if (page === 'stone') { try { pageStoneDetail(root, build, st); } catch (e) {} }
        if (page === 'settings') { try { pageSettings(root, build); } catch (e) {} }
      });
    } else if (page === 'stone') {
      pageStoneDetail(root, build, null);
    }
    try {
      if (page === 'settings') pageSettings(root, build);
      else if (page === 'stones') pageStones(root, build, root.getAttribute('data-rb-origin') || 'nat');
      else if (page === 'review') pageReview(root, build);
    } catch (err) {
      if (window.console) console.error('[ring-builder]', err);
      var b = root.querySelector('[data-rb-grid]') || root.querySelector('[data-rb-body]');
      if (b) b.innerHTML = '<div class="rb-empty">Something went wrong loading the builder. Please refresh.</div>';
    }
  });
})();
